import FormLabel from '@mui/material/FormLabel';
import React, { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import {
	Navigate,
	Route,
	Routes,
	useNavigate,
	useParams,
} from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import PolicyApi from '../../../api/PolicyApi';
import {
	AuthData,
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
} from '../../../context/auth';
import {
	PolicyContext,
	PolicyData,
	withPolicyContext,
} from '../../../context/policy';
import { socotraGray2 } from '../../../data/globalStylingVariables';
import AuxDataView from '../../auxData/AuxDataView';
import { StyledContentPane } from '../../common';
import DisabledOverlay from '../../DisabledOverlay';
import { useFastForm } from '../../fastForm';
import { getDefaultPolicyValues } from '../../fastForm/formBuilders';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import LeftSidebarLayout from '../../layouts/LeftSidebarLayout';
import QuoteContentLayout from '../../layouts/QuoteContentLayout';
import {
	CreatePaymentPage,
	EditPaymentPage,
	PaymentPage,
	PaymentsPage,
} from '../../payments';
import ProtectedRoute from '../../ProtectedRoute';
import { errorToast } from '../../Toast';
import QuoteDocumentsView from '../DocumentsView';
import Exposures from '../Exposures/Exposures';
import ReactSelect from '../fields/ReactSelect';
import FutureInvoiceView from '../FutureInvoices/FutureInvoiceView';
import InvoicesView from '../InvoicesView';
import InvoiceView from '../InvoiceView';
import PolicyDetails from '../PolicyDetails';
import PolicyInvoiceLayout from '../PolicyInvoiceLayout';
import PolicyPaymentsLayout from '../PolicyPaymentsLayout';
import QuotePricePreview from '../QuotePricePreview';
import QuoteSidebar from '../Sidebar/QuoteSidebar';
import ExposureRoutes from './ExposureRoutes';

interface Props {
	policyData: PolicyData;
	authData: AuthData;
}

// Acts as a MainLayout
// route: `/quotes/:quoteId`
const QuoteRoutes = (props: Props) => {
	const navigate = useNavigate();
	const params = useParams();
	const { policyData, authData } = props;
	const { hasPermission } = authData;
	const {
		quotes,
		quoteUpdateObj,
		selectedQuote,
		fields,
		loadData,
		saveFastForm,
	} = policyData;
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];

	const [loading, setLoading] = useState<boolean>(false);

	const methods = useFastForm(fields, {});
	const { policyId, quoteId } = params;
	props.policyData.setLoading = setLoading;
	const { getValues, reset, formState } = methods;
	const { dirtyFields, isDirty } = formState;

	const selectQuote = async (option: any) => {
		setLoading(true);
		try {
			try {
				isDirty &&
					(await saveFastForm(
						getValues(),
						dirtyFields as { [key: string]: boolean },
						reset,
					));
				await PolicyApi.selectQuote(
					authData.auth.authorizationToken,
					option.value,
				);
				const updatedPolicyData = await loadData(policyId);
				const defaultPolicyValues = getDefaultPolicyValues(
					updatedPolicyData.fields,
					props.authData,
				);
				reset(defaultPolicyValues);
				navigate(`${option.value}`);
			} catch (error) {
				if (await handleUnauthorizedResponse(error, authData)) {
					return;
				}
				errorToast('Failed to select quote');
			}
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}

		setLoading(false);
	};

	useEffect(() => {
		const populateForm = () => {
			if (fields) {
				const defaultEndorsementValues = getDefaultPolicyValues(
					fields,
					authData,
				);
				reset(defaultEndorsementValues);
			}
		};
		populateForm();
	}, [fields, reset, authData]);

	const renderMain = () => {
		if (!selectedQuote) {
			return <LargeCenterSpinner />;
		}

		return (
			<StyledContentPane>
				<QuoteContentLayout loading={loading}>
					<Routes>
						<Route
							path='pricing'
							element={
								<ProtectedRoute permission='policy.quote'>
									<QuotePricePreview />
								</ProtectedRoute>
							}
						/>
						<Route path='details' element={<PolicyDetails />} />
						<Route path='documents' element={<QuoteDocumentsView isQuote />} />
						<Route
							path='invoices/:invoiceId'
							element={
								<ProtectedRoute permission={'invoice.fetch'}>
									<InvoiceView />
								</ProtectedRoute>
							}
						/>
						<Route
							path='invoices'
							element={
								<ProtectedRoute permission={'invoice.fetch'}>
									<PolicyInvoiceLayout />
								</ProtectedRoute>
							}
						>
							<Route index element={<InvoicesView />} />
						</Route>
						<Route
							path='future-invoices'
							element={
								<ProtectedRoute permission={'invoice.fetch'}>
									<PolicyInvoiceLayout />
								</ProtectedRoute>
							}
						>
							<Route index element={<FutureInvoiceView />} />
						</Route>
						<Route
							path='exposures/:exposureId/*'
							element={<ExposureRoutes />}
						/>
						<Route path='exposures' element={<Exposures />} />
						<Route
							path='auxData'
							element={
								<ProtectedRoute permission='auxData.fetch'>
									<AuxDataView
										navigate={navigate}
										entityLocator={quoteId}
										entityType={'quote'}
									/>
								</ProtectedRoute>
							}
						/>
						{cashManagementEnabled && (
							<Route
								path='payments'
								element={
									<ProtectedRoute permission='payment.fetch'>
										<PolicyPaymentsLayout />
									</ProtectedRoute>
								}
							>
								<Route index element={<PaymentsPage />} />
								<Route path='new' element={<CreatePaymentPage />} />
								<Route path=':paymentLocator'>
									<Route index element={<PaymentPage />} />
									<Route path='edit' element={<EditPaymentPage />} />
								</Route>
							</Route>
						)}
						<Route path='*' element={<Navigate replace to='details' />} />
					</Routes>
				</QuoteContentLayout>
			</StyledContentPane>
		);
	};

	const renderSidebar = () => {
		if (!selectedQuote || !quoteUpdateObj) {
			return <DisabledOverlay />;
		}

		// inputValue
		const options: any = quotes.map((q) => ({
			value: q.locator,
			label: q.name,
		}));

		const customStyles = {
			container: (provided: any) => ({
				...provided,
				width: '100%',
			}),
		};

		return (
			<>
				<div style={{ margin: '0px 20px 10px 23px' }}>
					<FormLabel style={{ color: socotraGray2, fontSize: '14px' }}>
						Quote:
					</FormLabel>
					<ReactSelect
						styles={customStyles}
						options={options}
						isDisabled={!hasPermission('quote.quote')}
						value={{ value: selectedQuote.locator, label: selectedQuote.name }}
						onChange={selectQuote}
					/>
				</div>
				<QuoteSidebar loading={loading} />
			</>
		);
	};

	return methods ? (
		<FormProvider {...methods}>
			<PolicyContext.Provider value={policyData}>
				<LeftSidebarLayout
					leftSidebarContent={renderSidebar()}
					mainContent={renderMain()}
				/>
			</PolicyContext.Provider>
		</FormProvider>
	) : (
		<LargeCenterSpinner />
	);
};

export default withPolicyContext<Props>(QuoteRoutes);
